import { makeObservable, observable } from 'mobx';
import { IParameter, IParametersGroup, ParameterObject } from '.';
import { Parameter } from './Parameter';
import { ParametersGroup } from './ParametersGroup';

export function isParametersGroup(
  param: IParameter | IParametersGroup,
): param is IParametersGroup {
  return (param as IParametersGroup).parameters !== undefined;
}

export class ParametersStore {
  private static instance: ParametersStore;
  public static getInstance() {
    if (!ParametersStore.instance) {
      ParametersStore.instance = new ParametersStore();
    }
    return ParametersStore.instance;
  }

  private parameters: Record<string, Record<string, ParameterObject>> = {};

  private constructor() {
    makeObservable<ParametersStore, 'parameters'>(this, {
      parameters: observable,
    });
  }

  /**
   * Takes the string generated by the ParametersStore in the server
   * and assigns it to the current parameters
   */
  fromResponse(owner: string, response: string) {
    const params = JSON.parse(response) as Record<
      string,
      IParameter | IParametersGroup
    >;

    Object.entries(params).forEach(([name, param]) => {
      if (!this.parameters[owner]) {
        this.parameters[owner] = {};
      }

      if (isParametersGroup(param)) {
        this.parameters[owner][name] = new ParametersGroup(param);
      } else {
        this.parameters[owner][name] = new Parameter(param);
      }
    });
  }

  public static getParametersFromResponse(response: string) {
    const parsed = JSON.parse(response) as Record<
      string,
      IParameter | IParametersGroup
    >;
    const newParameters: Record<string, ParameterObject> = {};

    Object.entries(parsed).forEach(([name, param]) => {
      if (isParametersGroup(param)) {
        newParameters[name] = new ParametersGroup(param);
      } else {
        newParameters[name] = new Parameter(param);
      }
    });
    return newParameters;
  }

  public static getParametersFromObject(
    response: Record<string, IParameter | IParametersGroup>,
  ) {
    const newParameters: Record<string, ParameterObject> = {};

    Object.entries(response).forEach(([name, param]) => {
      if (param instanceof Parameter || param instanceof ParametersGroup) {
        newParameters[name] = param;
      } else {
        if (isParametersGroup(param)) {
          newParameters[name] = new ParametersGroup(param);
        } else {
          newParameters[name] = new Parameter(param);
        }
      }
    });
    return newParameters;
  }

  /**
   * Returns the parameters for a given owner name.
   */
  getParameters(ownerName: string) {
    return this.parameters[ownerName];
  }

  /**
   * Returns an object that can be used to submit the parameters to the server
   * in a format that the ParametersStore in the server expects.
   */
  getSubmitValue(owner: string) {
    const submitObject: Record<string, string[]> = {};

    Object.values(this.parameters[owner]).forEach((p) => {
      if (p instanceof Parameter) {
        submitObject[p.state.name] = p.getValues();
      } else if (p instanceof ParametersGroup) {
        for (const [name, param] of Object.entries(p.parameters)) {
          submitObject[name] = param.getValues();
        }
      }
    });

    return JSON.stringify(submitObject);
  }

  /**
   * Resets the parameters store.
   */
  reset() {
    this.parameters = {};
  }

  public static validate(parameters: Record<string, ParameterObject>) {
    let isValid = true;
    let firstInvalid: Parameter | null = null;

    for (const p of Object.values(parameters)) {
      if (p instanceof Parameter) {
        isValid = p.validate() && isValid;
        if (!firstInvalid && !isValid) {
          firstInvalid = p;
        }
      } else if (p instanceof ParametersGroup) {
        for (const pg of Object.values(p.parameters)) {
          isValid = pg.validate() && isValid;
          if (!firstInvalid && !isValid) {
            firstInvalid = pg;
          }
        }
      }
    }

    if (firstInvalid) {
      document
        .querySelector<HTMLElement>(`[name="${firstInvalid.state.name}"]`)
        ?.focus();
    }

    return isValid;
  }
}
